
import React, { PureComponent } from 'react';

/**
 * The number of bars to display in AudioWaveVisualizer.
 */
const AUDIO_LEVEL_BARS = 5;

/**
 * The type of the React {@code Component} props of {@link AudioWaveVisualizer}.
 */
type Props = {

  /**
   * The current audio level to display. The value should be a number between
   * 0 and 1.
   */
  audioLevel: number,
};

/**
 * Creates a ReactElement responsible for drawing audio levels similar to Google Meet.
 *
 * @augments {Component}
 */
class AudioLevelIndicator extends PureComponent<Props> {
    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const { audioLevel: passedAudioLevel } = this.props;

        // Ensure we have a valid audio level
        const audioLevel
            = typeof passedAudioLevel === 'number' && !isNaN(passedAudioLevel)
                ? Math.max(0, Math.min(passedAudioLevel, 1))
                : 0;

        // Apply sensitivity multiplier to make it more responsive (only if audioLevel > 0)
        const sensitiveAudioLevel = audioLevel > 0 ? Math.min(audioLevel * 2.5, 1) : 0;

        const audioLevelBars = [];

        for (let i = 0; i < AUDIO_LEVEL_BARS; i++) {
            // Calculate the threshold for each bar (progressive thresholds)
            const barThreshold = (i + 1) / AUDIO_LEVEL_BARS;

            // Determine if this bar should be active
            const isActive = sensitiveAudioLevel >= barThreshold;

            // Calculate height based on audio level and bar position
            let barHeight;

            if (isActive && audioLevel > 0) {
                // Active bars get progressively taller
                const baseHeight = (4 + i) * 2; // 4, 7, 10, 13, 16px
                const levelMultiplier = Math.max(0.3, sensitiveAudioLevel);

                barHeight = Math.round(baseHeight * levelMultiplier);
            } else {
                // Inactive bars or when muted get minimal height (dots)
                barHeight = 2;
            }

            // Calculate opacity
            let opacity;

            if (isActive && audioLevel > 0) {
                opacity = (0.7 + 0.3) * sensitiveAudioLevel; // 0.7 to 1.0
            } else {
                opacity = 0.2; // Very dim for inactive bars or when muted
            }

            // Add slight animation jitter for natural feel (only when audio is active)
            const jitter = isActive && audioLevel > 0
                ? (Math.random() - 0.5) * 0.1 * sensitiveAudioLevel
                : 0;
            const finalOpacity = Math.min(1, Math.max(0.1, opacity + jitter));

            // Color based on active state:
            // Inactive bars remain muted gray; active bars use MeetHour green (#1acc8d)
            let backgroundColor;

            if (!isActive || audioLevel === 0) {
                backgroundColor = '#6B7280'; // Gray for inactive or muted
            } else if (audioLevel > 0.9) {
                backgroundColor = '#F59E0B'; // Amber for peaking audio
            } else {
                backgroundColor = '#1acc8d'; // MeetHour signature green for active speech
            }

            const audioLevelBarsStyle = {
                width: '3px',
                height: `${barHeight}px`,
                backgroundColor,
                opacity: finalOpacity,
                borderRadius: '1.5px',
                margin: '0 1px',
                transition: 'height 0.12s ease-out, opacity 0.12s ease-out, background-color 0.15s ease-out',
                display: 'inline-block',
                verticalAlign: 'bottom',
                minHeight: '2px'
            };

            audioLevelBars.push(
                <div
                    className = 'audio-level-bar'
                    key = { i }
                    style = { audioLevelBarsStyle } />
            );
        }

        const AudioLevelIndicatorStyle = {
            display: 'inline-flex',
            alignItems: 'center',
            height: '20px',
            padding: '2px 2px 0px 6px',
            gap: '1px'
        };

        return (
            <div
                className = 'audio-level-indicator'
                style = { AudioLevelIndicatorStyle }>
                {audioLevelBars}
            </div>
        );
    }
}

export default AudioLevelIndicator;
